import {Injectable} from '@angular/core';
import {RichTextEditorComponent} from '@syncfusion/ej2-angular-richtexteditor';


export type EmojiSelectCallback = (emoji: string) => void;

interface ScrollState {
  container: HTMLElement | null;
  top: number;
}

/**
 * Emoji Picker Service
 * Provides a way to open the emoji picker from anywhere in the application.
 * We moved it into a service, because this is a Syncfusion component which is created and managed by a hidden RichTextEditorComponent and not a regular Angular component
 */

/**How to call the syncfusion meoji picker from anywhere */
/*private readonly emojiService: EmojiPickerService = inject(EmojiPickerService);

public onReactionSelection(event: MouseEvent): void {
    this.emojiService.openFromEvent(event, (emoji: string): void => {
      console.log('picked:', emoji);
      this.emoji = emoji
    });
  }*/

@Injectable({providedIn: 'root'})
export class EmojiPickerService {
  private static readonly MOBILE_VIEWPORT_WIDTH_THRESHOLD: number = 992;
  private static readonly KEYBOARD_HEIGHT_THRESHOLD: number = 120;
  private static readonly KEYBOARD_DETECTION_WINDOW_MS: number = 1500;
  private static readonly KEYBOARD_SCROLL_GRACE_MS: number = 900;

  private rte: RichTextEditorComponent | null = null;
  private activeCallback: EmojiSelectCallback | null = null;
  private popupEl: HTMLElement | null = null;
  private anchorRect: DOMRect | null = null;
  private popupOpenedAt: number = 0;
  private keyboardScrollGraceUntil: number = 0;
  private initialViewportWidth: number = 0;
  private initialViewportHeight: number = 0;
  private resizeListener: (() => void) | null = null;
  private visualViewportResizeListener: (() => void) | null = null;
  private visualViewportScrollListener: (() => void) | null = null;
  private scrollListener: ((ev: Event) => void) | null = null;
  private clickListener: ((ev: MouseEvent) => void) | null = null;


  public registerRte(rte: RichTextEditorComponent): void {
    this.rte = rte;
  }

  public openFromEvent(event: MouseEvent, onSelect: EmojiSelectCallback): void {
    const anchor: HTMLElement | null = this.resolveAnchorFromEvent(event);
    if (!anchor) {
      return;
    }

    const rect: DOMRect = anchor.getBoundingClientRect();
    this.open(anchor, rect, onSelect);
  }

  /**
   * Open the emoji picker.
   * @param anchor Element used to find the correct container who initiated the click
   * @param rect   DOMRect of the anchor, calculate in the caller (component)
   */
  public open(anchor: HTMLElement, rect: DOMRect, onSelect: EmojiSelectCallback): void {
    if (!this.rte) {
      console.warn('EmojiPickerService: RTE not registered (app-emoji-picker missing?).');
      return;
    }

    this.closePopup();
    this.activeCallback = onSelect;
    this.anchorRect = rect;
    this.popupOpenedAt = Date.now();
    this.keyboardScrollGraceUntil = 0;
    this.initialViewportWidth = this.getViewportWidth();
    this.initialViewportHeight = this.getViewportHeight();

    const scrollState: ScrollState = this.captureScrollState(anchor);

    const emojiBtn: HTMLElement | null =
      this.rte.element.querySelector('.e-toolbar .e-emoji');
    emojiBtn?.click();

    setTimeout((): void => {
      this.restoreScrollState(scrollState);
      this.positionPopup(rect);
      this.attachGlobalListeners();
    }, 0);
  }

  public closePopup(): void {
    if (this.popupEl) {
      this.popupEl.style.display = 'none';
      this.popupEl = null;
    }

    this.detachGlobalListeners();
    this.activeCallback = null;
    this.anchorRect = null;
    this.popupOpenedAt = 0;
    this.keyboardScrollGraceUntil = 0;
    this.initialViewportWidth = 0;
    this.initialViewportHeight = 0;
  }

   // ----------------------------
  // Anchor resolving, needed because we can initiate from any custom component too
  // ----------------------------

  private resolveAnchorFromEvent(event: MouseEvent): HTMLElement | null {
    const path: EventTarget[] = event.composedPath();

    // Prefer an actual <button> or role="button"
    const btn: HTMLElement | undefined = path.find((p: EventTarget): p is HTMLElement =>
      p instanceof HTMLElement &&
      (p.tagName === 'BUTTON' || p.getAttribute('role') === 'button')
    );
    if (btn) {
      return btn;
    }

    // Otherwise, pick the first element with a real layout box
    const el: HTMLElement | undefined = path.find((p: EventTarget): p is HTMLElement => {
      if (!(p instanceof HTMLElement)) {
        return false;
      }
      const r: DOMRect = p.getBoundingClientRect();
      return r.width > 0 && r.height > 0;
    });

    // Fallback: target
    if (el) {
      return el;
    }

    return event.target instanceof HTMLElement ? event.target : null;
  }

  // ----------------------------
  // Positioning
  // ----------------------------

  private positionPopup(rect: DOMRect): void {
    const popup: HTMLElement | null = document.querySelector(
      '.e-rte-emojipicker-popup.e-popup-open'
    );

    if (!popup) {
      setTimeout(() => this.positionPopup(rect), 30);
      return;
    }

    this.popupEl = popup;

    popup.style.position = 'fixed';
    popup.style.display = 'block';
    popup.style.opacity = '1';
    popup.style.visibility = 'visible';
    popup.style.zIndex = String(this.getMaxZIndexInDocument() + 1);

    const viewportWidth: number = this.getViewportWidth();
    const viewportHeight: number = this.getViewportHeight();

    const popupWidth: number = popup.offsetWidth || 360;
    const popupHeight: number = popup.offsetHeight || 360;

    // center over trigger row
    let left: number = rect.left + (rect.width - popupWidth) / 2;
    // prefer below trigger row
    let top: number = rect.bottom + 8;

    // clamp X
    if (left < 0) {
      left = 0;
    }
    if (left + popupWidth > viewportWidth) {
      left = viewportWidth - popupWidth;
    }

    // open above if needed
    if (top + popupHeight > viewportHeight) {
      top = rect.top - popupHeight - 8;
    }
    if (top < 0) {
      top = 0;
    }

    popup.style.left = `${left}px`;
    popup.style.top = `${top}px`;

    popup.onclick = (event: MouseEvent): void => {
      const target: HTMLElement | null = event.target as HTMLElement;
      const emoji: string | undefined = target?.textContent?.trim();

      if (emoji && this.activeCallback) {
        this.activeCallback(emoji);
      }
      this.closePopup();
    };
  }

  // ----------------------------
  // Global close rules
  // ----------------------------

  private attachGlobalListeners(): void {
    this.resizeListener = (): void => this.handleViewportChange();
    window.addEventListener('resize', this.resizeListener);

    if (window.visualViewport) {
      this.visualViewportResizeListener = (): void => this.handleViewportChange();
      this.visualViewportScrollListener = (): void => this.handleViewportChange();
      window.visualViewport.addEventListener('resize', this.visualViewportResizeListener);
      window.visualViewport.addEventListener('scroll', this.visualViewportScrollListener);
    }

    // close on scroll outside the popup
    this.scrollListener = (event: Event): void => {
      if (!this.popupEl) {
        return;
      }
      if (Date.now() < this.keyboardScrollGraceUntil) {
        return;
      }
      const target: EventTarget | null = event.target;
      const inside: boolean = target instanceof Node && this.popupEl.contains(target);
      if (!inside) {
        this.closePopup();
      }
    };
    window.addEventListener('scroll', this.scrollListener, true);

    // close on outside click
    this.clickListener = (event: MouseEvent): void => {
      if (!this.popupEl) {
        return;
      }
      const target: HTMLElement = event.target as HTMLElement;
      const inside: boolean = this.popupEl.contains(target);
      if (!inside) {
        this.closePopup();
      }
    };
    document.addEventListener('click', this.clickListener, true);
  }

  private detachGlobalListeners(): void {
    if (this.resizeListener) {
      window.removeEventListener('resize', this.resizeListener);
      this.resizeListener = null;
    }
    if (this.visualViewportResizeListener && window.visualViewport) {
      window.visualViewport.removeEventListener('resize', this.visualViewportResizeListener);
      this.visualViewportResizeListener = null;
    }
    if (this.visualViewportScrollListener && window.visualViewport) {
      window.visualViewport.removeEventListener('scroll', this.visualViewportScrollListener);
      this.visualViewportScrollListener = null;
    }
    if (this.scrollListener) {
      window.removeEventListener('scroll', this.scrollListener, true);
      this.scrollListener = null;
    }
    if (this.clickListener) {
      document.removeEventListener('click', this.clickListener, true);
      this.clickListener = null;
    }
  }

  // ----------------------------
  // Scroll jump prevention
  // ----------------------------

  private captureScrollState(anchor: HTMLElement): ScrollState {
    const container: HTMLElement | null = this.findScrollContainer(anchor);
    const top: number = container?.scrollTop ?? window.scrollY;
    return {container, top};
  }

  private restoreScrollState(state: ScrollState): void {
    if (state.container) {
      state.container.scrollTop = state.top;
    } else {
      window.scrollTo({top: state.top});
    }
  }

  private findScrollContainer(start: HTMLElement): HTMLElement | null {
    let el: HTMLElement | null = start;

    while (el && el !== document.body) {
      const style: CSSStyleDeclaration = window.getComputedStyle(el);
      const overflowY: string = style.overflowY;

      if (overflowY === 'auto' || overflowY === 'scroll') {
        return el;
      }

      el = el.parentElement;
    }

    return null;
  }

  private getMaxZIndexInDocument(): number {
    let maxZ: number = 0;
    const els: NodeListOf<HTMLElement> = document.querySelectorAll<HTMLElement>('body *');

    els.forEach((el: HTMLElement) => {
      const zStr: string = window.getComputedStyle(el).zIndex;
      if (!zStr || zStr === 'auto') {
        return;
      }
      const z: number = Number(zStr);
      if (!Number.isNaN(z) && z > maxZ) {
        maxZ = z;
      }
    });

    return maxZ;
  }

  private handleViewportChange(): void {
    if (!this.popupEl || !this.anchorRect) {
      return;
    }

    if (this.shouldKeepPopupOpenDuringKeyboardTransition()) {
      this.keyboardScrollGraceUntil = Date.now() + EmojiPickerService.KEYBOARD_SCROLL_GRACE_MS;
      this.positionPopup(this.anchorRect);
      return;
    }

    this.closePopup();
  }

  private shouldKeepPopupOpenDuringKeyboardTransition(): boolean {
    const openedRecently: boolean =
      (Date.now() - this.popupOpenedAt) <= EmojiPickerService.KEYBOARD_DETECTION_WINDOW_MS;
    if (!openedRecently) {
      return false;
    }

    const baseWidth: number = this.initialViewportWidth || this.getViewportWidth();
    const baseHeight: number = this.initialViewportHeight || this.getViewportHeight();
    const currentWidth: number = this.getViewportWidth();
    const currentHeight: number = this.getViewportHeight();
    const heightShrink: number = baseHeight - currentHeight;
    const widthDelta: number = Math.abs(baseWidth - currentWidth);
    const isMobileViewport: boolean = Math.min(baseWidth, currentWidth) <= EmojiPickerService.MOBILE_VIEWPORT_WIDTH_THRESHOLD;

    return isMobileViewport &&
      heightShrink >= EmojiPickerService.KEYBOARD_HEIGHT_THRESHOLD &&
      widthDelta < EmojiPickerService.KEYBOARD_HEIGHT_THRESHOLD;
  }

  private getViewportWidth(): number {
    return window.visualViewport?.width ?? window.innerWidth;
  }

  private getViewportHeight(): number {
    return window.visualViewport?.height ?? window.innerHeight;
  }
}
